<template>
  <span v-if="canShowTasksIn3d" class="task-isolate-button">
    <!--
      Loading skeleton — reserves the slot during the first in-flight fetch so
      the card layout doesn't shift when the button appears or stays hidden.
      Mounted fetch (see mounted hook) populates isolationInfo, after which
      shouldShowIsolateButton drives the real render below.
    -->
    <div v-if="loading && !dataFetched" class="task-isolate-button__skeleton" aria-hidden="true">
      <!--
        Hand-drawn isometric cube outline (hexagon + inner Y). All edges
        share a synchronized opacity "breathing" pulse — quietest of the
        showcase options, reads as "thinking" rather than "loading."
        Cheap on the GPU compositor: only opacity animates, no layout/paint.
      -->
      <svg
        viewBox="0 0 24 24"
        class="task-isolate-button__skeleton-svg"
        xmlns="http://www.w3.org/2000/svg">
        <g
          fill="none"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round">
          <polygon
            points="12,2 20.5,7 20.5,17 12,22 3.5,17 3.5,7"
            class="task-isolate-button__skeleton-edge" />
          <polyline points="12,12 12,2" class="task-isolate-button__skeleton-edge" />
          <polyline points="12,12 20.5,7" class="task-isolate-button__skeleton-edge" />
          <polyline points="12,12 3.5,7" class="task-isolate-button__skeleton-edge" />
        </g>
      </svg>
    </div>

    <!--
      Real button — visible only when the aggregated isolation info shows
      something to isolate (mirrors TagCard.isolate computed semantics for
      the multi-tag aggregate that taskIsolation.js produces).
    -->
    <v-tooltip v-else-if="shouldShowIsolateButton" right>
      <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
        <v-menu
          v-model="showFacilityMenu"
          :close-on-content-click="true"
          offset-y
          left
          transition="scale-transition">
          <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
            <v-btn
              :color="buttonColor"
              icon
              small
              v-bind="{
                ...tooltipAttrs,
                ...(clickBehavior === 'navigate-menu' ? menuAttrs : {}),
              }"
              v-on="{
                ...tooltipOn,
                ...(clickBehavior === 'navigate-menu' ? menuOn : {}),
              }"
              :class="{ 'isolate-btn--cross-facility': buttonColor === 'warning' }"
              @click.stop="handleClick"
              @contextmenu.prevent.stop="handleIsolateRightClick">
              <v-icon small>{{ iconName }}</v-icon>
            </v-btn>
          </template>

          <!-- Facility Selection Menu (only used when click-behavior === 'navigate-menu') -->
          <v-card class="facility-menu" elevation="8">
            <v-list dense class="facility-menu__list">
              <v-subheader class="facility-menu__header">
                <v-icon small class="mr-2">mdi-map-marker-multiple</v-icon>
                Open in Facility
              </v-subheader>
              <v-divider></v-divider>
              <v-list-item
                v-for="facility in otherFacilities"
                :key="facility.id"
                class="facility-menu__item"
                @click="openFacilityInNewTab(facility.id)">
                <v-list-item-icon class="mr-3">
                  <v-icon small color="warning">mdi-open-in-new</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="facility-menu__name">{{
                    facility.name
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>
  </span>
</template>

<script>
  // @workflow tasks.show-in-3d
  import {
    getTaskIsolationInfo,
    getIsolateClickBehavior,
    getIsolateButtonColor,
    getIsolateTooltip,
    getIsolateIcon,
    getTaskEffectiveTagIds,
    mapFacilitiesToObjects,
    awaitInFlightPrefetch,
  } from '@Task/utils/taskIsolation';
  import { log as debugLog, error as debugError } from '@/utils/debugLogger';
  import { showTasksInViewer, openTaskInFacility } from '@/actions/tasks';
  import { CAPABILITY, can } from '@/actions/permissions';

  export default {
    name: 'TaskIsolateButton',

    props: {
      /**
       * Task object with forgeTagsIds array (and id for cache-key invalidation).
       */
      task: {
        type: Object,
        required: true,
      },
    },

    data() {
      return {
        loading: false,
        isolationInfo: null,
        dataFetched: false,
        showFacilityMenu: false,
      };
    },

    computed: {
      /** Show tasks in 3D: the permission map (actions/permissions.js). */
      canShowTasksIn3d() {
        return can(CAPABILITY.TASKS_SHOW_IN_3D, this.$store);
      },
      /**
       * Facility dictionary (id → name) from store.
       */
      facilityDict() {
        return this.$store.getters.getFacilityDict || {};
      },

      /**
       * Click behavior derived from isolation info.
       * 'none' / 'isolate' / 'navigate-menu' (see taskIsolation.js).
       */
      clickBehavior() {
        return getIsolateClickBehavior(this.isolationInfo);
      },

      /**
       * Vuetify color: 'primary' or 'warning' (orange when cross-facility).
       */
      buttonColor() {
        return getIsolateButtonColor(this.isolationInfo);
      },

      /**
       * Tooltip text varies per behavior; mirrors TagCard.isolateTooltip variants.
       */
      tooltipText() {
        return getIsolateTooltip(this.isolationInfo, this.facilityDict);
      },

      /**
       * Icon varies by which viewer the click will open:
       *  - mdi-cube-outline                       → 3D isolation (harvested data)
       *  - mdi-image-filter-center-focus  → 360 isolation (positioned-only data)
       *  - mdi-loupe                      → fallback (no data yet)
       * See taskIsolation.getIsolateIcon for the full decision tree.
       */
      iconName() {
        return getIsolateIcon(this.isolationInfo);
      },

      /**
       * Aggregate visibility gate. Mirrors TagCard.isolate but adapted for the
       * multi-tag aggregate produced by taskIsolation.getTaskIsolationInfo.
       *
       * Hide when:
       *  - Initial fetch is still in flight (skeleton handles the slot)
       *  - No tags / fetch failed
       *  - Aggregate has nothing to isolate anywhere (no current-facility harvest
       *    AND no other-facility availability) — analogous to TagCard's
       *    "no children + not harvested" hide.
       */
      shouldShowIsolateButton() {
        if (this.loading && !this.dataFetched) return false;
        if (!this.isolationInfo) return false;
        const { combinedAssets, hasCurrentFacility } = this.isolationInfo;
        if (!combinedAssets?.length && !hasCurrentFacility) return false;
        return true;
      },

      /**
       * Facility list for the cross-facility menu. Mirrors TagCard.otherFacilities.
       */
      otherFacilities() {
        return mapFacilitiesToObjects(this.isolationInfo?.otherAssets ?? [], this.facilityDict);
      },
    },

    mounted() {
      debugLog('TaskIsolateButton', 'mounted, kicking off ensureDataFetched', {
        taskId: this.task?.id,
        tagCount: this.task?.forgeTagsIds?.length ?? 0,
      });
      this.ensureDataFetched();
    },

    methods: {
      /**
       * Fetch and cache isolation info for the linked tags. Idempotent — early
       * return if already fetched or in flight. Cache-keyed by sorted tag-id set
       * inside taskIsolation.js, so multiple cards sharing tags do one fetch total.
       */
      async ensureDataFetched() {
        if (this.dataFetched || this.loading) return;
        if (!this.task?.id && !this.task?.forgeTagsIds?.length) {
          // Unsaved task with no direct tag has no server identity to resolve.
          this.dataFetched = true;
          return;
        }

        this.loading = true;
        try {
          // Wait for any board-level batched prefetch that may be in flight so
          // we don't race-fall-back to the legacy per-task path. If no prefetch
          // is running, this resolves immediately.
          await awaitInFlightPrefetch();
          this.isolationInfo = await getTaskIsolationInfo(this.task, this.$store);
          this.dataFetched = true;
          debugLog('TaskIsolateButton', 'data fetched', {
            taskId: this.task?.id,
            shouldShow: this.shouldShowIsolateButton,
            buttonColor: this.buttonColor,
            clickBehavior: this.clickBehavior,
            otherFacilitiesCount: this.otherFacilities.length,
          });
        } catch (err) {
          debugError('TaskIsolateButton: Failed to fetch isolation info', err);
        } finally {
          this.loading = false;
        }
      },

      /**
       * Left-click handler — branches by click behavior. The button is hidden
       * for 'none' so this method only sees 'isolate' or 'navigate-menu'.
       */
      async handleClick() {
        // Defensive ensure: covers race conditions where mount fired but
        // resolution is still pending (rare given the skeleton hides the button).
        await this.ensureDataFetched();
        const behavior = this.clickBehavior;

        debugLog('TaskIsolateButton', 'handleClick', {
          behavior,
          taskId: this.task?.id,
        });

        if (behavior === 'isolate') {
          // The one show-tasks action (actions/tasks.js), as on Kanban and Gantt.
          showTasksInViewer({ store: this.$store }, { tasks: [this.task], statusId: null });
        } else if (behavior === 'navigate-menu') {
          // Inline v-menu opens via menuAttrs/menuOn binding; explicit set covers
          // the path where the menu wasn't activator-bound (defensive).
          this.showFacilityMenu = true;
        }
        // 'none' is unreachable here because shouldShowIsolateButton hides the button.
      },

      /**
       * Right-click handler — opens the facility menu only when there are other
       * facilities to navigate to. Matches TagCard.handleIsolateRightClick.
       */
      async handleIsolateRightClick() {
        await this.ensureDataFetched();
        debugLog('TaskIsolateButton', 'handleIsolateRightClick', {
          otherFacilitiesCount: this.otherFacilities.length,
          taskId: this.task?.id,
        });
        if (this.otherFacilities.length > 0) {
          this.showFacilityMenu = true;
        }
        // No-op when no other facilities — matches TagCard semantics.
      },

      /**
       * Opens the chosen facility in a new tab with isolation context. Wraps the
       * shared navigateToFacility helper so the parent can react via the
       * 'navigate' event (used by some hosts that track cross-tab handoff).
       */
      openFacilityInNewTab(facilityId) {
        const tagIds = getTaskEffectiveTagIds(this.task, this.isolationInfo);
        debugLog('TaskIsolateButton', 'openFacilityInNewTab', { facilityId, tagIds });
        openTaskInFacility(this.$router, this.task, this.isolationInfo, facilityId);
        this.$emit('navigate', { facilityId, tagIds });
      },
    },

    watch: {
      /**
       * Reset and re-fetch when the bound task identity changes. The cache-key
       * inside taskIsolation.js (sorted tag-id list) handles same-tags reuse.
       */
      'task.id': {
        handler() {
          this.dataFetched = false;
          this.isolationInfo = null;
          this.ensureDataFetched();
        },
      },
      'task.forgeTagsIds': {
        handler() {
          this.dataFetched = false;
          this.isolationInfo = null;
          this.ensureDataFetched();
        },
        deep: true,
      },
    },
  };
</script>

<style lang="scss" scoped>
  // ═══════════════════════════════════════════════════════════
  // TASK ISOLATE BUTTON — Aligned with TagCard isolation styling.
  // Icon (mdi-loupe) is preserved for task identity; coloring,
  // visibility, and interaction mirror TagCard.
  // ═══════════════════════════════════════════════════════════

  .task-isolate-button {
    display: inline-flex;
    align-items: center;
  }

  // ─── Loading skeleton (cube outline + breathing pulse) ───
  // Reserves the slot during the in-flight fetch. Hand-drawn isometric cube
  // SVG: every edge shares a synchronized opacity-pulse animation. The pulse
  // reads as "thinking" while staying out of the user's way. Only opacity
  // animates, so this is essentially free on the GPU compositor.
  .task-isolate-button__skeleton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
  }

  .task-isolate-button__skeleton-svg {
    width: 22px;
    height: 22px;
    overflow: visible;
  }

  .task-isolate-button__skeleton-edge {
    stroke: var(--v-primary-base);
    animation: task-isolate-cube-breathe 1.6s ease-in-out infinite;
  }

  @keyframes task-isolate-cube-breathe {
    0%,
    100% {
      opacity: 0.25;
    }
    50% {
      opacity: 1;
    }
  }

  // Accessibility: pin to a midpoint when the user prefers reduced motion.
  @media (prefers-reduced-motion: reduce) {
    .task-isolate-button__skeleton-edge {
      animation: none;
      opacity: 0.6;
    }
  }

  // ─── Orange Button for Cross-Facility Tags (mirrors TagCard:820-833) ───
  .isolate-btn--cross-facility {
    position: relative;

    ::v-deep .v-icon {
      color: #d97706 !important;
      transition: all 0.2s ease;
    }

    &:hover ::v-deep .v-icon {
      color: #b45309 !important;
      transform: scale(1.1);
    }
  }

  // ─── Facility Selection Menu (mirrors TagCard:835-876) ───
  .facility-menu {
    min-width: 200px;
    max-width: 320px;
    border-radius: 8px !important;
    overflow: hidden;
    border: 1px solid rgba(217, 119, 6, 0.2);

    &__list {
      padding: 0 !important;
    }

    &__header {
      background: linear-gradient(135deg, rgba(217, 119, 6, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%);
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #d97706 !important;
      padding: 12px 16px !important;
      height: auto !important;
    }

    &__item {
      transition: all 0.15s ease;
      min-height: 44px;

      &:hover {
        background: rgba(217, 119, 6, 0.08) !important;
      }

      &:active {
        background: rgba(217, 119, 6, 0.15) !important;
      }
    }

    &__name {
      font-weight: 500;
      font-size: 13px;
      color: inherit;
    }
  }

  // ─── Theme-aware adjustments (mirrors TagCard:878-895) ───
  // Note: the skeleton edges intentionally use var(--v-primary-base) in both
  // themes — the opacity pulse provides enough contrast without needing a
  // theme-specific color override.
  .theme--dark {

    .facility-menu {
      border-color: rgba(217, 119, 6, 0.3);

      &__header {
        background: linear-gradient(
          135deg,
          rgba(217, 119, 6, 0.15) 0%,
          rgba(217, 119, 6, 0.08) 100%
        );
      }

      &__item:hover {
        background: rgba(217, 119, 6, 0.12) !important;
      }
    }
  }
</style>
